import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import get from 'lodash/get';

import { SuiContainer } from '@splashthat/sui';

import { colorAppFieldSecondaryBackground } from 'common/styles/colors';
import { containerPaddingS, containerPaddingXL } from 'common/styles/variables';
import { getTicketStylesByCategory } from 'common/entities/form/styleConfiguration/selectors';
import { isFormTypeTicketSelection } from 'common/entities/form/selectors';
import { getLimitOneOrderPerEmail } from 'common/compounds/FormRenderer/selectors';
import { getFormSessionStepsEnabled } from 'common/entities/form/sessionRegistration';
import { getFormId, getFormContentTypeNodes, getOpenStylingSection } from '../../selectors';
import { setOpenStyleEditorSection, setOpenFormTicketStyling } from '../../actions';
import * as formStylePanels from '../../constants/formStylePanels';

import FormStyleCollapsibles from './FormStyleCollapsibles';
import FormStyleEditorTicketHeader from './FormStyleEditorTicketHeader';
import {
    FormStyleEditorFormPanel,
    FormStyleEditorFormFieldsPanel,
    FormStyleEditorButtonPanel,
    FormStyleEditorHeadlinePanel,
    FormStyleEditorTicketEmailPanel,
    FormStyleEditorErrorsPanel,
    FormStyleEditorTextElementPanel,
    FormStyleEditorStepperPanel,
    FormStyleEditorSessionDetailsPanel,
    FormStyleEditorSecondaryButtonPanel,
} from './FormStyleEditorPanels';

import './FormStyleEditor.styl';

export const TICKET_SELECTION_FORM = 'ticketSelection';

const FORM_STYLES_LABEL = 'Form Styles';
const TICKET_STYLES_LABEL = 'Ticket Styles';
const BASE_CLASS_NAME = 'FormStyleEditor';

const TicketTypeStylingMenu = styled.div`
    background-color: ${colorAppFieldSecondaryBackground};
    padding: ${`${containerPaddingS} ${containerPaddingXL}`};
    font-weight: bold;
`;

/** @var {Object} - react components for collapsible contents by collapsible identifier. */
const collapsibleFormContentComponents = {
    [formStylePanels.FORM]: <FormStyleEditorFormPanel />,
    [formStylePanels.HEADLINE]: <FormStyleEditorHeadlinePanel />,
    [formStylePanels.FORM_FIELDS]: <FormStyleEditorFormFieldsPanel />,
    [formStylePanels.TEXT_ELEMENT]: <FormStyleEditorTextElementPanel />,
    [formStylePanels.TICKET_EMAIL]: <FormStyleEditorTicketEmailPanel />,
    [formStylePanels.BUTTON]: <FormStyleEditorButtonPanel />,
    [formStylePanels.ERRORS]: <FormStyleEditorErrorsPanel />,
};

const collapsibleFormSessionContentComponents = {
    [formStylePanels.STEPPER]: <FormStyleEditorStepperPanel />,
    [formStylePanels.HEADLINES]: <FormStyleEditorHeadlinePanel />,
    [formStylePanels.SESSION_DETAILS]: <FormStyleEditorSessionDetailsPanel />,
    [formStylePanels.PRIMARY_BUTTON]: <FormStyleEditorButtonPanel />,
    [formStylePanels.SECONDARY_BUTTON]: <FormStyleEditorSecondaryButtonPanel />,
};

class FormStyleEditor extends Component {
    static propTypes = {
        formId: PropTypes.string.isRequired,
        hasContentTypeNodes: PropTypes.bool.isRequired,
        openCollapsible: PropTypes.oneOf(formStylePanels.styleEditorPanels),
        setOpenStyleEditorSection: PropTypes.func.isRequired,
        setOpenFormTicketStyling: PropTypes.func.isRequired,
        isTicketSelectionForm: PropTypes.bool.isRequired,
        ticketTypeStylesByCategory: PropTypes.array.isRequired,
        limitOneOrderPerEmail: PropTypes.bool.isRequired,
        isFormSessionEnabled: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        openCollapsible: null,
    };

    shouldRenderPanel(identifier, panelConditions) {
        return typeof panelConditions[identifier] !== 'undefined' ? panelConditions[identifier] : true;
    }

    openTicketStyleTabs(ticketPanel) {
        this.props.setOpenStyleEditorSection(ticketPanel);
        this.props.setOpenFormTicketStyling(this.props.formId, ticketPanel);
    }

    getFilteredPanels = (panels, conditions) =>
        panels.filter((identifier) => this.shouldRenderPanel(identifier, conditions));

    render() {
        const {
            isTicketSelectionForm,
            hasContentTypeNodes,
            openCollapsible,
            limitOneOrderPerEmail,
            isFormSessionEnabled,
        } = this.props;

        const panelConditions = {
            [formStylePanels.TEXT_ELEMENT]: hasContentTypeNodes,
            [formStylePanels.FORM_FIELDS]: !isTicketSelectionForm,
            [formStylePanels.TICKET_EMAIL]: isTicketSelectionForm && limitOneOrderPerEmail,
        };

        const { formSessionPanels, formPanels } = formStylePanels;

        const filteredFormPanels = isFormSessionEnabled
            ? this.getFilteredPanels(formSessionPanels, panelConditions)
            : this.getFilteredPanels(formPanels, panelConditions);

        return (
            <SuiContainer className={BASE_CLASS_NAME} data-qa={BASE_CLASS_NAME}>
                {isTicketSelectionForm && <TicketTypeStylingMenu>{FORM_STYLES_LABEL}</TicketTypeStylingMenu>}
                <FormStyleCollapsibles
                    panels={filteredFormPanels}
                    collapsibleContentComponents={{
                        ...collapsibleFormContentComponents,
                        ...collapsibleFormSessionContentComponents,
                    }}
                    openCollapsible={openCollapsible}
                    setOpenStyleEditorSection={this.props.setOpenStyleEditorSection}
                />

                {isTicketSelectionForm && (
                    <SuiContainer>
                        <TicketTypeStylingMenu>{TICKET_STYLES_LABEL}</TicketTypeStylingMenu>
                        {formStylePanels.ticketPanels.map((ticketPanel) => {
                            const ticketTypeStyleByCategory = this.props.ticketTypeStylesByCategory.find(
                                (ticketTypeStyle) => ticketTypeStyle.type === ticketPanel,
                            );

                            return (
                                <FormStyleEditorTicketHeader
                                    key={ticketPanel}
                                    handleOnClick={() => this.openTicketStyleTabs(ticketPanel)}
                                    label={formStylePanels.collapsibleLabels[ticketPanel]}
                                    subLabel={formStylePanels.collapsibleSubLabels[ticketPanel]}
                                    lastEdited={get(ticketTypeStyleByCategory, 'lastEdited')}
                                    ticketPanelType={ticketPanel}
                                />
                            );
                        })}
                    </SuiContainer>
                )}
            </SuiContainer>
        );
    }
}

const mapStateToProps = (state, props) => {
    const formId = getFormId(state);

    const isTicketSelectionForm = isFormTypeTicketSelection(state, formId);
    const contentTypeNodes = getFormContentTypeNodes(state, formId);
    const openCollapsible = getOpenStylingSection(state);
    const ticketTypeStylesByCategory = formStylePanels.ticketPanels.map((ticketPanel) =>
        getTicketStylesByCategory(state, formId, ticketPanel),
    );
    const limitOneOrderPerEmail = getLimitOneOrderPerEmail(state);
    const isFormSessionEnabled = getFormSessionStepsEnabled(state, formId);

    return {
        formId,
        openCollapsible,
        hasContentTypeNodes: !!contentTypeNodes.length,
        isTicketSelectionForm,
        ticketTypeStylesByCategory,
        limitOneOrderPerEmail,
        isFormSessionEnabled,
    };
};

const mapDispatchToProps = {
    setOpenStyleEditorSection,
    setOpenFormTicketStyling,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleEditor);
